import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings, ShieldCheck, DownloadCloud, HelpCircle, BarChart3, Globe, Moon, LogOut, Wallet } from 'lucide-react';
import { api, type Status } from '../lib/api';
import { useStatus, fmtUsd } from '../lib/useStatus';
import { setLang, LANGS } from '../i18n';
import { getTheme, setTheme, watchSystemTheme, THEMES, type ThemeMode } from '../lib/theme';

/** Entrée simple du menu (action). */
function Item({
  icon: Icon,
  label,
  onClick,
  danger,
}: {
  icon: typeof HelpCircle;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition ${
        danger ? 'text-red-600 hover:bg-red-500/10 dark:text-red-400' : 'text-fg hover:bg-surface-2'
      }`}
    >
      <Icon className={`h-4 w-4 shrink-0 ${danger ? '' : 'text-muted'}`} /> {label}
    </button>
  );
}

/** Ligne « réglage » : libellé à gauche, segmenté à droite (langue, thème). */
function SegRow<T extends string>({
  icon: Icon,
  label,
  value,
  options,
  onChange,
}: {
  icon: typeof Globe;
  label: string;
  value: T;
  options: { v: T; l: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 px-3 py-2">
      <span className="flex items-center gap-2.5 text-sm text-fg">
        <Icon className="h-4 w-4 shrink-0 text-muted" /> {label}
      </span>
      <div className="inline-flex shrink-0 rounded-lg bg-surface-2 p-0.5">
        {options.map((o) => (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            aria-pressed={value === o.v}
            className={`rounded-md px-2 py-0.5 text-xs font-medium transition ${
              value === o.v ? 'bg-surface text-fg shadow-sm' : 'text-muted hover:text-fg'
            }`}
          >
            {o.l}
          </button>
        ))}
      </div>
    </div>
  );
}

const Divider = () => <div className="my-1.5 h-px bg-border" />;

/** Solde IA en tête de menu : vue d'ensemble sans quitter la roue dentée. */
function BudgetRow({ status }: { status: Status }) {
  const { t } = useTranslation();
  const { spend, budget } = status;
  const low = budget.low;
  const pct = budget.monthly ? Math.min(100, Math.round((spend.month / budget.monthly) * 100)) : null;
  return (
    <div className={`rounded-xl px-3 py-2.5 ${low ? 'bg-red-500/10 ring-1 ring-red-500/30' : 'bg-surface-2'}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-sm font-medium text-fg">
          <Wallet className="h-4 w-4 text-muted" /> {t('status.budgetTitle')}
        </span>
        <span className={`text-sm font-semibold tabular-nums ${low ? 'text-red-600 dark:text-red-400' : 'text-fg'}`}>
          {budget.remaining != null ? fmtUsd(budget.remaining) : fmtUsd(spend.month)}
        </span>
      </div>
      {pct != null && (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface">
          <div className={`h-full rounded-full transition-all ${low ? 'bg-red-500' : 'bg-accent'}`} style={{ width: `${pct}%` }} />
        </div>
      )}
      <p className="mt-1.5 text-xs text-muted">
        {budget.monthly != null ? t('status.spentMonth', { amount: fmtUsd(spend.month) }) : `${fmtUsd(spend.month)} · ${t('status.noBudget')}`}
      </p>
    </div>
  );
}

/**
 * Réglages sous la roue dentée : on regroupe ici tout ce qui n'est pas de la
 * navigation (aide, outils, langue, thème, déconnexion) pour désencombrer la
 * barre du haut. Les entrées d'outils sont filtrées par capacité : « Accès &
 * rôles » (admin), « Importations » (importateur) — un importateur non-admin ne
 * voit donc que l'import.
 */
export function GearMenu({
  isAdmin,
  canImport,
  authed,
  onHelp,
  onInsights,
  onAccess,
  onImport,
}: {
  isAdmin: boolean;
  canImport: boolean;
  authed: boolean;
  onHelp: () => void;
  onInsights: () => void;
  onAccess: () => void;
  onImport: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [theme, setThemeState] = useState<ThemeMode>(getTheme);
  const ref = useRef<HTMLDivElement>(null);
  const status = useStatus();
  const budgetLow = status?.budget.low ?? false;

  // Fermeture au clic extérieur et à Échap.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // En mode auto, le thème suit le système même app ouverte.
  useEffect(() => watchSystemTheme(), []);

  const pickTheme = (m: ThemeMode): void => {
    setTheme(m);
    setThemeState(m);
  };
  const lang = i18n.language?.startsWith('de') ? 'de' : 'fr';
  const act = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };
  const hasTools = isAdmin || canImport;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={t('menu.settings')}
        aria-expanded={open}
        aria-haspopup="menu"
        title={t('menu.settings')}
        className={`relative grid h-9 w-9 place-items-center rounded-full transition ${
          open ? 'text-fg ring-2 ring-accent' : 'text-muted hover:bg-surface-2 hover:text-fg'
        }`}
      >
        <Settings className="h-[18px] w-[18px]" />
        {budgetLow && (
          <span aria-hidden="true" className="absolute right-0.5 top-0.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-surface" />
        )}
      </button>

      {open && (
        <div role="menu" className="absolute right-0 z-40 mt-2 w-64 rounded-2xl bg-surface p-1.5 shadow-xl ring-1 ring-border">
          {status && (
            <>
              <BudgetRow status={status} />
              <Divider />
            </>
          )}
          <Item icon={HelpCircle} label={t('help.open')} onClick={act(onHelp)} />

          {hasTools && <Divider />}
          {isAdmin && <Item icon={BarChart3} label={t('insights.open')} onClick={act(onInsights)} />}
          {isAdmin && <Item icon={ShieldCheck} label={t('access.open')} onClick={act(onAccess)} />}
          {canImport && <Item icon={DownloadCloud} label={t('import.open')} onClick={act(onImport)} />}

          <Divider />
          <SegRow
            icon={Globe}
            label={t('lang.label')}
            value={lang}
            options={LANGS.map((l) => ({ v: l, l: l.toUpperCase() }))}
            onChange={(v) => setLang(v)}
          />
          <SegRow
            icon={Moon}
            label={t('theme.label')}
            value={theme}
            options={THEMES.map((m) => ({ v: m, l: t(`theme.${m}`) }))}
            onChange={pickTheme}
          />

          {authed && (
            <>
              <Divider />
              <Item
                icon={LogOut}
                label={t('auth.logout')}
                danger
                onClick={() => void api.auth.logout().then(() => location.reload())}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
